// ============================================================
// VCE — Función de decisión (el CORAZÓN del motor)
// ============================================================
// P4: el motor DECIDE, no aplica CSS. El motor es un motor de
// decisión; el render es solo la consecuencia (ratificado 13:36).
//
// Semántica de evaluación:
//   - Reglas BASE (registry): PRIMERA coincidencia gana por campo.
//     El seed = R8 (defaults); las reglas específicas (R1-R7) solo
//     pueden cambiar un campo desde su default. R8 jamás sobrescribe.
//   - Reglas EXTRA del tenant (config.extraRules): override consciente
//     (última gana) — P4 sin forks, trazable.
//   - mediaRatio se resuelve de token semántico → concreto vía config.
//   - fit lo resuelve fitPolicy (contain SIEMPRE por defecto).
//
// Función PURA: sin DOM, sin estado, sin efectos — headless-ready.
// ============================================================
import type {
  CompositionDecision,
  ContentProfile,
  DecisionPatch,
  MediaCapabilities,
  MediaRatioToken,
  SmartCardVariant,
  TenantVisualConfig,
} from '../types';
import { BASE_DECISION_RULES } from '../registries/decisionRules';
import { resolveFit } from '../policies/fitPolicy';

// Valores por defecto (semilla = R8 del registry)
const DEFAULT_VARIANT: SmartCardVariant = 'default';
const DEFAULT_TOKEN: MediaRatioToken = 'default';
const DEFAULT_TREATMENT: CompositionDecision['infoTreatment'] = 'compact';
const DEFAULT_PRIORITY: CompositionDecision['actionPriority'] = 'normal';

// Ratios concretos por token (fallback si el tenant no configura)
const RATIO_BY_TOKEN: Record<MediaRatioToken, string> = {
  default: '3/4',
  heavy: '1/1',
  square: '1/1',
};

const VARIANT_RATIO_FALLBACK: Partial<Record<SmartCardVariant, string>> = {
  default: '3/4',
  'media-left': '1/1',
  compact: '1/1',
  hero: '16/9',
};

interface MutableDecision {
  variant: SmartCardVariant;
  mediaRatioToken: MediaRatioToken;
  infoTreatment: CompositionDecision['infoTreatment'];
  actionPriority: CompositionDecision['actionPriority'];
  fit: CompositionDecision['fit'];
  capabilities: MediaCapabilities;
}

/** Aplica un patch: mode 'first' = solo desde default (reglas base);
 *  mode 'last' = override consciente (reglas extra del tenant). */
function applyPatch(d: MutableDecision, p: DecisionPatch, mode: 'first' | 'last'): void {
  const set = <K extends keyof MutableDecision>(key: K, val: MutableDecision[K] | undefined, defaultValue: MutableDecision[K]) => {
    if (val === undefined) return;
    if (mode === 'last' || d[key] === defaultValue) d[key] = val;
  };
  set('variant', p.variant, DEFAULT_VARIANT);
  set('mediaRatioToken', p.mediaRatio, DEFAULT_TOKEN);
  set('infoTreatment', p.infoTreatment, DEFAULT_TREATMENT);
  set('actionPriority', p.actionPriority, DEFAULT_PRIORITY);
  set('fit', p.fit, 'contain' as MutableDecision['fit']);

  // capabilities: first-wins por clave en base; last-wins en extra
  for (const [k, v] of Object.entries(p.capabilities ?? {})) {
    const key = k as keyof MediaCapabilities;
    if (mode === 'last' || d.capabilities[key] === undefined) {
      (d.capabilities as Record<string, unknown>)[key] = v;
    }
  }
}

/** Resuelve el token de ratio → valor concreto (config del tenant primero) */
function resolveRatio(token: MediaRatioToken, config: TenantVisualConfig, variant: SmartCardVariant): string {
  if (token === 'heavy') return config.mediaRatios?.heavy ?? RATIO_BY_TOKEN.heavy;
  if (token === 'square') return config.mediaRatios?.default ?? RATIO_BY_TOKEN.square;
  // default: preferencia del tenant, si no sugerencia de la variante, si no 3/4
  return config.mediaRatios?.default ?? VARIANT_RATIO_FALLBACK[variant] ?? RATIO_BY_TOKEN.default;
}

export function composeDecision(
  profile: ContentProfile,
  config: TenantVisualConfig = {},
): CompositionDecision {
  // Semilla = R8 (defaults del motor)
  const d: MutableDecision = {
    variant: DEFAULT_VARIANT,
    mediaRatioToken: DEFAULT_TOKEN,
    infoTreatment: DEFAULT_TREATMENT,
    actionPriority: DEFAULT_PRIORITY,
    fit: resolveFit(config),
    capabilities: {},
  };

  // 1. Reglas BASE en orden de prioridad (primera coincidencia gana por campo)
  for (const rule of BASE_DECISION_RULES) {
    if (rule.when(profile)) applyPatch(d, rule.then, 'first');
  }

  // 2. Reglas EXTRA del tenant (override consciente y trazable)
  for (const rule of config.extraRules ?? []) {
    if (rule.when(profile)) applyPatch(d, rule.then, 'last');
  }

  // 3. Resolución final: token → concreto + fit
  return {
    variant: d.variant,
    mediaRatio: resolveRatio(d.mediaRatioToken, config, d.variant),
    capabilities: d.capabilities,
    infoTreatment: d.infoTreatment,
    actionPriority: d.actionPriority,
    fit: d.fit,
  };
}
